/**
 * RouteMapGoogle — Google Maps flavour of RouteMap.
 *
 * Same prop contract as ../routes/RouteMap.jsx so the parent can hot-swap
 * between OpenStreetMap (Leaflet) and Google Maps without changing shape.
 *
 * Props:
 *   customers  — [{id, name, customer_code, lat, lng, selected?}]
 *   start      — {lat, lng, label?}     (optional)
 *   end        — {lat, lng, label?}     (optional)
 *   route      — [{seq, lat, lng, customer_name, status?}]  (optional)
 *   onPinClick — (customer) => void
 *   height     — css height string (default "420px")
 */
import React, { useEffect, useRef, useState } from "react";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Shared loader — same pattern as staff-app/TaskMap.jsx to avoid double
// injection when both components mount on the same page.
let _mapsPromise = null;
function loadGoogleMaps() {
  if (_mapsPromise) return _mapsPromise;
  _mapsPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject("no window");
    if (window.google?.maps) return resolve(window.google.maps);
    if (!GMAPS_KEY) return reject("REACT_APP_GOOGLE_MAPS_API_KEY missing");
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GMAPS_KEY}&libraries=geometry`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject("Google Maps failed to load");
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

export default function RouteMapGoogle({
  customers = [], start, end, route, onPinClick, height = "420px",
}) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef([]);          // Markers + polyline handles for cleanup
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState("");

  // Load the JS API once
  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(e => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, []);

  // Init the map div
  useEffect(() => {
    if (!ready || !divRef.current || mapRef.current) return;
    mapRef.current = new window.google.maps.Map(divRef.current, {
      center: { lat: 9.93, lng: 76.26 },  // Kochi fallback
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: "greedy",
    });
  }, [ready]);

  // (Re-)render markers + polyline every time inputs change
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const G = window.google.maps;

    // Clear previous layers
    layersRef.current.forEach(l => l.setMap?.(null));
    layersRef.current = [];

    const bounds = new G.LatLngBounds();
    let hasBounds = false;

    // Unsequenced customer pins (Create-plan mode)
    if (!route) {
      customers.forEach(c => {
        if (c.lat == null || c.lng == null) return;
        const marker = new G.Marker({
          position: { lat: Number(c.lat), lng: Number(c.lng) },
          map: mapRef.current,
          title: `${c.name || ""}${c.customer_code ? " · " + c.customer_code : ""}`,
          icon: {
            path: G.SymbolPath.CIRCLE,
            scale: c.selected ? 10 : 7,
            fillColor: c.selected ? "#C25D38" : "#3A7033",
            fillOpacity: 1,
            strokeColor: "#FFFFFF",
            strokeWeight: 2,
          },
        });
        marker.addListener("click", () => onPinClick?.(c));
        layersRef.current.push(marker);
        bounds.extend({ lat: Number(c.lat), lng: Number(c.lng) });
        hasBounds = true;
      });
    }

    // Numbered stops when a route is provided
    if (route && route.length) {
      route.forEach(s => {
        if (s.lat == null || s.lng == null) return;
        const color = s.status === "completed" ? "#2D5A27"
                    : s.status === "skipped"   ? "#C25D38"
                    :                            "#3A7033";
        const marker = new G.Marker({
          position: { lat: Number(s.lat), lng: Number(s.lng) },
          map: mapRef.current,
          label: { text: String(s.seq), color: "#FFFFFF", fontSize: "11px", fontWeight: "bold" },
          icon: {
            path: G.SymbolPath.CIRCLE, scale: 12, fillColor: color,
            fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 2,
          },
          title: `#${s.seq} · ${s.customer_name || ""}`,
        });
        layersRef.current.push(marker);
        bounds.extend({ lat: Number(s.lat), lng: Number(s.lng) });
        hasBounds = true;
      });
    }

    // Start / End markers
    if (start?.lat != null) {
      const m = new G.Marker({
        position: { lat: Number(start.lat), lng: Number(start.lng) },
        map: mapRef.current,
        title: `Start${start.label ? " · " + start.label : ""}`,
        icon: {
          path: G.SymbolPath.CIRCLE, scale: 11, fillColor: "#1F3A57",
          fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3,
        },
        zIndex: 900,
      });
      layersRef.current.push(m);
      bounds.extend({ lat: Number(start.lat), lng: Number(start.lng) });
      hasBounds = true;
    }
    if (end?.lat != null && (end.lat !== start?.lat || end.lng !== start?.lng)) {
      const m = new G.Marker({
        position: { lat: Number(end.lat), lng: Number(end.lng) },
        map: mapRef.current,
        title: `End${end.label ? " · " + end.label : ""}`,
        icon: {
          path: G.SymbolPath.CIRCLE, scale: 11, fillColor: "#7A2E18",
          fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3,
        },
      });
      layersRef.current.push(m);
      bounds.extend({ lat: Number(end.lat), lng: Number(end.lng) });
      hasBounds = true;
    }

    // Polyline start → …stops → end
    if (route && route.length) {
      const path = [];
      if (start?.lat != null) path.push({ lat: Number(start.lat), lng: Number(start.lng) });
      route.forEach(s => {
        if (s.lat != null && s.lng != null) path.push({ lat: Number(s.lat), lng: Number(s.lng) });
      });
      if (end?.lat != null) path.push({ lat: Number(end.lat), lng: Number(end.lng) });
      else if (start?.lat != null) path.push({ lat: Number(start.lat), lng: Number(start.lng) });
      const line = new G.Polyline({
        path, map: mapRef.current,
        strokeColor: "#2D5A27", strokeOpacity: 0.85, strokeWeight: 3,
        icons: [{ icon: { path: G.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3 },
                   offset: "50%", repeat: "80px" }],
      });
      layersRef.current.push(line);
    }

    if (hasBounds) {
      try { mapRef.current.fitBounds(bounds, 40); } catch { /* noop */ }
    }
  }, [ready, customers, start, end, route, onPinClick]);

  return (
    <div style={{ height }} data-testid="route-map-google"
         className="relative rounded-md overflow-hidden border border-[#E5E2D9] bg-[#F0EFEA]">
      {!ready && !err && (
        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#5C5A55]">
          Loading Google Maps…
        </div>
      )}
      {err && (
        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#7A2E18] px-4 text-center">
          Google Maps failed to load — falling back to OSM would be safer.<br/>
          <span className="text-[#5C5A55]">{err}</span>
        </div>
      )}
      <div ref={divRef} className="w-full h-full"/>
    </div>
  );
}
